/**
 * API utility functions for building URLs and handling direct backend access.
 *
 * These helpers are particularly important for file downloads and direct browser
 * requests that bypass the Create React App proxy.
 */

import axios, { AxiosInstance } from 'axios';

/**
 * Get the API base URL from environment variables.
 *
 * In Docker: Uses empty string (relative URLs work via proxy)
 * In local dev: Set REACT_APP_API_BASE_URL=http://localhost:9010 in .env.local
 *
 * @returns The API base URL or empty string for relative URLs
 */
export const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL || '';
};

/**
 * Configured axios instance with authentication interceptors.
 *
 * Automatically handles:
 * - Cookie-based authentication (withCredentials: true)
 * - 401 responses (redirects to login)
 * - 403 responses (shows error message)
 */
export const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true, // Send cookies with requests
});

// Response interceptor for handling authentication errors
api.interceptors.response.use(
  (response) => response, // Pass through successful responses
  (error) => {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;

      // Handle authentication errors
      if (status === 401) {
        // In development mode, don't redirect - let components handle the error
        // This prevents conflicts with the dev mode auth bypass in App.tsx
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Dev Mode] 401 Unauthorized - component should handle this error');
          return Promise.reject(error);
        }

        // Clear login state
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('user');

        // Carry the current location so the login lands the operator back where
        // they were. Without this every session expiry dumps you on the
        // dashboard root — during a live broadcast that costs the Live-Admin
        // operator a navigation they do not have time for.
        const here = window.location.pathname + window.location.search;
        const target =
          here && here !== '/' && here !== '/login'
            ? `/login?next=${encodeURIComponent(here)}`
            : '/login';
        window.location.href = target;
      }

      // Handle forbidden errors
      if (status === 403) {
        console.error('Access forbidden:', error.response.data);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Build a complete download URL with query parameters.
 *
 * Use this for window.open() and anchor href attributes where the browser
 * makes direct requests that bypass axios/proxy.
 *
 * @param endpoint - The API endpoint path (e.g., "/api/vnr/snapshots/download-proof")
 * @param params - Query parameters as key-value pairs
 * @returns Complete URL with base URL and query string
 */
export const buildDownloadUrl = (endpoint: string, params: Record<string, string>): string => {
  const baseUrl = getApiBaseUrl();
  const queryString = new URLSearchParams(params).toString();
  return `${baseUrl}${endpoint}?${queryString}`;
};

/**
 * Generic fetch helper that wraps axios and returns typed data.
 *
 * @param url - The API endpoint URL
 * @param options - Optional fetch options (method, body, headers)
 * @returns Promise with success status and data or error
 */
export async function fetchData<T>(
  url: string,
  options?: {
    method?: string;
    body?: string;
    headers?: Record<string, string>;
  },
): Promise<{
  success: boolean;
  data?: T;
  error?: string;
  /** Soft-Guard des Backends: der Aufrufer darf mit einem Bestätigungs-Flag erneut senden. */
  requires_confirmation?: boolean;
}> {
  try {
    const config: any = {
      url,
      method: options?.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    };

    if (options?.body) {
      config.data = JSON.parse(options.body);
    }

    const response = await api.request(config);
    return response.data;
  } catch (error: any) {
    const payload = error.response?.data;
    const errorMessage = payload?.error || error.message || 'Unknown error';
    // Zusatzfelder der Fehlerantwort durchreichen — ohne sie kann der Aufrufer
    // einen bestätigbaren Soft-Guard (409 requires_confirmation) nicht von einem
    // harten Fehler unterscheiden, und die Bestätigung wird zur Sackgasse.
    // Nur echte Objekte spreaden: antwortet der Server mit einer HTML-Fehlerseite
    // (nginx 502, Proxy-Timeout), ist payload ein String und der Spread zerlegt
    // ihn in nummerierte Schlüssel — genau die Character-Map, die im Repo als
    // Signatur für doppelt serialisiertes JSON gilt und die Fehlersuche fehlleitet.
    // success/error stehen NACH dem Spread, damit ein Fehler-Body sie nicht
    // überschreiben kann.
    const extra = payload && typeof payload === 'object' ? payload : {};
    return { ...extra, success: false, error: errorMessage };
  }
}

/**
 * POST helper that wraps fetchData with POST method and JSON body.
 *
 * @param url - The API endpoint URL
 * @param data - The data object to send as JSON body
 * @returns Promise with success status and data or error
 */
export async function postData<T>(
  url: string,
  data: object,
): Promise<{ success: boolean; data?: T; error?: string; message?: string }> {
  try {
    const response = await api.post(url, data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
    return { success: false, error: errorMessage };
  }
}
